import React, { Fragment, useState } from 'react';
import MainLayout from '../layout/MainLayout';
import { motion } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import {
  Mountain,
  PlaneTakeoff,
  HeartHandshake,
  ShieldCheck,
  Map
} from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import Drone3Img from '../assets/drone3.jpg'


const tours = [
  {
    title: 'Gorilla Trekking in Bwindi',
    location: 'Bwindi Impenetrable Forest',
    duration: '3 Days / 2 Nights',
    price: '$1,200',
    image: '/images/tours/gorilla.jpg'
  },
  {
    title: 'Murchison Falls Safari',
    location: 'Murchison Falls National Park',
    duration: '4 Days / 3 Nights',
    price: '$980',
    image: '/images/tours/murchison.jpg'
  },
  {
    title: 'Source of the Nile Adventure',
    location: 'Jinja',
    duration: '2 Days / 1 Night',
    price: '$600',
    image: '/images/tours/nile.jpg'
  },
  {
    title: 'Rwenzori Mountains Hike',
    location: 'Kasese',
    duration: '5 Days / 4 Nights',
    price: '$1,500',
    image: '/images/tours/rwenzori.jpg'
  }
];

export const OurTours = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);

  const openDialog = (tour) => {
    setSelectedTour(tour);
    setIsOpen(true);
  };

  const closeDialog = () => setIsOpen(false);

  return (
    <MainLayout>
      {/* Hero */}
      <section className="relative z-10 h-[75vh] rounded-3xl mx-4 sm:mx-10 mb-16 overflow-hidden shadow-xl">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${Drone3Img})` }}
        />
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-center p-6 sm:p-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-white max-w-3xl"
          >
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">
              Discover Uganda's Wild Soul
            </h1>
            <p className="text-lg sm:text-xl mb-6">
              Handcrafted experiences — from mountain trails to jungle mist.
            </p>
            <a
              href="#tours"
              className="inline-block px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition"
            >
              Browse Tours
            </a>
          </motion.div>
        </div>
      </section>

      {/* Filter Tags */}
      <section className="max-w-7xl mx-auto px-4 mb-10">
        <div className="flex flex-wrap justify-center gap-4">
          {['All', 'Wildlife', 'Culture', 'Adventure', 'Luxury'].map((tag, i) => (
            <span
              key={i}
              className="px-4 py-2 rounded-full bg-white/20 dark:bg-white/10 backdrop-blur border border-white/30 dark:border-white/10 text-sm cursor-pointer hover:bg-white/30 dark:hover:bg-white/20 transition"
            >
              {tag}
            </span>
          ))}
        </div>
      </section>

      {/* Tour Cards */}
      <section
        id="tours"
        className="max-w-7xl mx-auto px-4 grid gap-10 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mb-24"
      >
        {tours.map((tour, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden shadow-md backdrop-blur-xl bg-white/30 dark:bg-white/10 border border-white/20 dark:border-white/10"
          >
            <LazyLoadImage
              src={tour.image}
              alt={tour.title}
              effect="blur"
              className="h-56 w-full object-cover"
            />
            <div className="p-5 space-y-2 text-sm">
              <h3 className="text-lg font-semibold">{tour.title}</h3>
              <p className="text-gray-500 dark:text-gray-300">{tour.location}</p>
              <p className="text-gray-700 dark:text-gray-400">{tour.duration}</p>
              <p className="font-bold text-teal-600">{tour.price}</p>
              <button
                onClick={() => openDialog(tour)}
                className="mt-2 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition w-full text-center"
              >
                View Details
              </button>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Why Muhaad */}
      <section className="max-w-6xl mx-auto px-4 text-center mb-20">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold mb-8"
        >
          Why Choose Muhaad?
        </motion.h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-10">
          {[
            { icon: <Mountain />, text: 'Local Roots, Global Standards' },
            { icon: <HeartHandshake />, text: 'Cultural Integrity & Respect' },
            { icon: <ShieldCheck />, text: 'Trusted Guides & Safety First' },
            { icon: <Map />, text: 'Rare, Custom Routes' },
            { icon: <PlaneTakeoff />, text: 'Smooth Logistics, End to End' }
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white/20 dark:bg-white/10 backdrop-blur p-6 rounded-xl border border-white/20 dark:border-white/10"
            >
              <div className="flex justify-center mb-3 text-teal-500">{item.icon}</div>
              <p className="text-sm">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Custom CTA */}
      <section className="text-center px-4 pb-20">
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-2xl font-semibold mb-4"
        >
          Not sure which tour is right for you?
        </motion.h3>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          Let us build a custom Ugandan experience tailored to your style and schedule.
        </p>
        <a
          href="/contact"
          className="inline-block px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-lg"
        >
          Plan My Trip
        </a>
      </section>

      {/* Tour Dialog */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 text-left align-middle shadow-xl transition-all border border-white/20 dark:border-white/10">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    {selectedTour?.title}
                  </Dialog.Title>
                  <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 space-y-2">
                    <p><strong>Location:</strong> {selectedTour?.location}</p>
                    <p><strong>Duration:</strong> {selectedTour?.duration}</p>
                    <p><strong>Price:</strong> {selectedTour?.price}</p>
                    <p>This is a preview. Get in touch to customize or book this tour.</p>
                  </div>
                  <div className="mt-4 text-right">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 transition"
                      onClick={closeDialog}
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </MainLayout>
  );
};
